import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { EnergyModeConfig } from '../types'

type FormatMessageFunction = (descriptor: { id: string }, values?: Record<string, any>) => string

// Pure config object using CSS variables - single source of truth
// All colors reference CSS variables defined in energy-level.less
export interface EnergyModeVisualConfig {
  primaryColor: string // CSS variable reference
  accentColor: string // CSS variable reference
  backgroundColor: string // CSS variable reference
  textColor: string // CSS variable reference
  textSecondaryColor: string // CSS variable reference
}

export const ENERGY_MODE_VISUAL_CONFIG: Partial<Record<EnergyMode, EnergyModeVisualConfig>> = {
  [EnergyMode.PEAK]: {
    primaryColor: 'var(--energy-peak-primary)',
    accentColor: 'var(--energy-peak-accent)',
    backgroundColor: 'var(--energy-peak-bg)',
    textColor: 'var(--energy-peak-text)',
    textSecondaryColor: 'var(--energy-peak-text-secondary)',
  },
  [EnergyMode.ENERGIZED]: {
    primaryColor: 'var(--energy-energized-primary)',
    accentColor: 'var(--energy-energized-accent)',
    backgroundColor: 'var(--energy-energized-bg)',
    textColor: 'var(--energy-energized-text)',
    textSecondaryColor: 'var(--energy-energized-text-secondary)',
  },
  [EnergyMode.FUNCTIONAL]: {
    primaryColor: 'var(--energy-functional-primary)',
    accentColor: 'var(--energy-functional-accent)',
    backgroundColor: 'var(--energy-functional-bg)',
    textColor: 'var(--energy-functional-text)',
    textSecondaryColor: 'var(--energy-functional-text-secondary)',
  },
  [EnergyMode.STRUGGLING]: {
    primaryColor: 'var(--energy-struggling-primary)',
    accentColor: 'var(--energy-struggling-accent)',
    backgroundColor: 'var(--energy-struggling-bg)',
    textColor: 'var(--energy-struggling-text)',
    textSecondaryColor: 'var(--energy-struggling-text-secondary)',
  },
  [EnergyMode.DRAINED]: {
    primaryColor: 'var(--energy-drained-primary)',
    accentColor: 'var(--energy-drained-accent)',
    backgroundColor: 'var(--energy-drained-bg)',
    textColor: 'var(--energy-drained-text)',
    textSecondaryColor: 'var(--energy-drained-text-secondary)',
  },
}

// Helper function to get visual config by score
export const getEnergyModeVisualConfig = (score: number): EnergyModeVisualConfig => {
  const mode = getEnergyMode(score)
  const config = ENERGY_MODE_VISUAL_CONFIG[mode]
  if (!config) {
    throw new Error(`No visual config found for energy mode: ${mode}`)
  }
  return config
}

// Helper function to get visual config by mode
export const getVisualConfigByMode = (mode: EnergyMode): EnergyModeVisualConfig => {
  const config = ENERGY_MODE_VISUAL_CONFIG[mode]
  if (!config) {
    throw new Error(`No visual config found for energy mode: ${mode}`)
  }
  return config
}

// Helper function to get energy mode display name
// Returns a function that maps EnergyMode to localized string
export const getEnergyModeNameMap = (formatMessage: FormatMessageFunction): Record<EnergyMode, string> => {
  return {
    [EnergyMode.PEAK]: formatMessage({ id: 'energyMode.peak.name' }),
    [EnergyMode.ENERGIZED]: formatMessage({ id: 'energyMode.energized.name' }),
    [EnergyMode.FUNCTIONAL]: formatMessage({ id: 'energyMode.functional.name' }),
    [EnergyMode.STRUGGLING]: formatMessage({ id: 'energyMode.struggling.name' }),
    [EnergyMode.DRAINED]: formatMessage({ id: 'energyMode.drained.name' }),
  }
}

// Helper function to get energy mode display name by score
export const getEnergyModeName = (score: number, formatMessage: FormatMessageFunction): string => {
  const mode = getEnergyMode(score)
  const nameMap = getEnergyModeNameMap(formatMessage)
  return nameMap[mode] || ''
}

// Helper function to get energy mode display name by mode
export const getEnergyModeNameByMode = (mode: EnergyMode, formatMessage: FormatMessageFunction): string => {
  const nameMap = getEnergyModeNameMap(formatMessage)
  return nameMap[mode] || ''
}

// Updated getEnergyModeConfig - now uses CSS variables from centralized config and includes name
export const getEnergyModeConfig = (score: number, formatMessage: FormatMessageFunction): EnergyModeConfig => {
  const mode = getEnergyMode(score)
  const visualConfig = ENERGY_MODE_VISUAL_CONFIG[mode]
  const nameMap = getEnergyModeNameMap(formatMessage)

  if (!visualConfig) {
    throw new Error(`No visual config found for energy mode: ${mode}`)
  }

  const name = nameMap[mode]
  if (!name) {
    throw new Error(`No name found for energy mode: ${mode}`)
  }

  switch(mode) {
    case EnergyMode.PEAK: {
      return {
        ...visualConfig,
        mode,
        name,
        topBannerMessage: formatMessage({ id: 'energyMode.peak.topBannerMessage' }),
        humanCenteredMessage: formatMessage({ id: 'energyMode.peak.humanCenteredMessage' }),
      }
    }
    case EnergyMode.ENERGIZED: {
      return {
        ...visualConfig,
        mode,
        name,
        topBannerMessage: formatMessage({ id: 'energyMode.energized.topBannerMessage' }),
        humanCenteredMessage: formatMessage({ id: 'energyMode.energized.humanCenteredMessage' }),
      }
    }
    case EnergyMode.FUNCTIONAL: {
      return {
        ...visualConfig,
        mode,
        name,
        topBannerMessage: formatMessage({ id: 'energyMode.functional.topBannerMessage' }),
        humanCenteredMessage: formatMessage({ id: 'energyMode.functional.humanCenteredMessage' }),
      }
    }
    case EnergyMode.STRUGGLING: {
      return {
        ...visualConfig,
        mode,
        name,
        topBannerMessage: formatMessage({ id: 'energyMode.struggling.topBannerMessage' }),
        humanCenteredMessage: formatMessage({ id: 'energyMode.struggling.humanCenteredMessage' }),
      }
    }
    case EnergyMode.DRAINED: {
      return {
        ...visualConfig,
        mode,
        name,
        topBannerMessage: formatMessage({ id: 'energyMode.drained.topBannerMessage' }),
        humanCenteredMessage: formatMessage({ id: 'energyMode.drained.humanCenteredMessage' }),
      }
    }
    default: {
      // These should not be returned by getEnergyMode, but handle gracefully if they are
      throw new Error(`Unsupported energy mode: ${mode}`)
    }
  }
}
